/**
 * Story 8.1 — o NÚCLEO PURO do formulário de Bónus: a definição servida pelo Engine e a validação
 * espelhada. Sem React, sem rede, sem DOM.
 *
 * Porque é puro: o harness do storefront é `vitest` com `environment: node` e `include:
 * ["src/**\/*.test.ts"]` — **só `.ts`**. Não existe jsdom, RTL nem Playwright neste repo, logo JSX é
 * INTESTÁVEL aqui. Toda a regra do formulário vive por isso neste módulo, e os componentes ficam a
 * ser só a renderização dela. Precedente: `src/lib/util/fiscal.ts` + `fiscal.test.ts`.
 *
 * ESTA VALIDAÇÃO É UX, NÃO É A LEI. A autoridade é o Engine
 * (`apps/backend/src/modules/bonus/lib/bonus-form.ts`): o servidor revalida tudo o que chega e é ele
 * que decide. O que está aqui existe para o utilizador ver o erro no campo certo antes de esperar por
 * um round-trip.
 *
 * O QUE VEM DA API vs. O QUE ESTÁ COPIADO À MÃO — a distinção é o que impede uma falsa sensação de
 * fonte única:
 *   · VEM de `GET /store/bonus-form`, por campo: `name`, `label`, `type`, `required`, `maxLength`,
 *     `options` e `helpText`. Mudar a definição no Engine muda o ecrã E as duas validações sem tocar
 *     em código — é aqui, e só aqui, que "uma regra, dois sítios" é literal.
 *   · ESTÁ COPIADO À MÃO deste lado: a união `BONUS_FORM_FIELD_TYPES`, o regex de e-mail e TODAS as
 *     mensagens de erro. É outro pacote (não há import possível do backend) e NADA testa que os dois
 *     concordam — divergirem é possível, e nenhum teste o apanharia.
 *
 * Por isso o modo de FALHAR é que tem de ser seguro, e é — mas só para as REGRAS (regex, mensagens): o
 * Engine revalida tudo e recusa com a SUA mensagem PT-BR, que a rota devolve também na chave `message` (a
 * única que o `@medusajs/js-sdk` mostra). Uma regra que divirja aqui degrada para o erro do servidor —
 * nunca para um "válido" no browser que o servidor recusa em silêncio.
 *
 * A UNIÃO DE TIPOS não partilha esse modo de falhar, e é honesto dizê-lo: `parseBonusFormFields` é
 * tudo-ou-nada, logo um `type` que o Engine sirva e esta união não conheça derruba a página INTEIRA para
 * o erro de carregamento — antes de haver submissão, logo sem o servidor chegar a ser a autoridade. É
 * deliberado (ver `parseBonusFormFields`) e é o lado seguro: renderizar só os campos reconhecidos
 * esconderia um campo que o Engine pode exigir, e aí NENHUMA submissão passaria, com o utilizador a ler
 * um erro sobre um campo que não está no ecrã. O preço é que um `type` novo exige deploy dos DOIS lados
 * — acrescentar um campo de um tipo JÁ conhecido (o caso comum) não exige nada deste lado.
 */

/** Espelha `BONUS_FORM_FIELD_TYPES` do Engine. Duplicado por ser outro pacote (não há import possível). */
export const BONUS_FORM_FIELD_TYPES = ["text", "email", "tel", "select", "textarea"] as const

export type BonusFormFieldType = (typeof BONUS_FORM_FIELD_TYPES)[number]

/** Espelha `BonusFormField` do Engine. É o contrato de `GET /store/bonus-form`. */
export type BonusFormField = {
  name: string
  label: string
  type: BonusFormFieldType
  required: boolean
  maxLength: number
  options?: readonly string[]
  helpText?: string
  // Vem da definição (nunca adivinhado do `type`): o `type` diz como o campo se comporta, não o que
  // significa — e é o significado que decide o autofill. Ver o comentário no `constants.ts` do Engine.
  autoComplete?: string
}

/** Valores do formulário: `name` do campo → o texto tal como o utilizador o escreveu. */
export type BonusFormValues = Record<string, string>

/** Erros: `name` do campo → mensagem PT-BR. Um campo ausente do mapa está válido. */
export type BonusFormErrors = Record<string, string>

// Espelha `BONUS_EMAIL_PATTERN` do Engine — MESMA regra, deliberadamente permissiva. Se divergir, o
// servidor ganha (e o utilizador vê um erro que o browser não previu, que é o modo de falhar certo:
// nunca o contrário — nunca um "válido" no browser que o servidor recusa em silêncio).
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const isBonusFormFieldType = (value: unknown): value is BonusFormFieldType =>
  typeof value === "string" && (BONUS_FORM_FIELD_TYPES as readonly string[]).includes(value)

const isBonusFormField = (value: unknown): value is BonusFormField => {
  if (typeof value !== "object" || value === null) {
    return false
  }
  const field = value as Record<string, unknown>
  const optionsOk =
    field.options === undefined ||
    (Array.isArray(field.options) && field.options.every((option) => typeof option === "string"))
  return (
    typeof field.name === "string" &&
    typeof field.label === "string" &&
    isBonusFormFieldType(field.type) &&
    typeof field.required === "boolean" &&
    typeof field.maxLength === "number" &&
    optionsOk &&
    (field.helpText === undefined || typeof field.helpText === "string") &&
    (field.autoComplete === undefined || typeof field.autoComplete === "string")
  )
}

/**
 * Estreita a resposta crua de `GET /store/bonus-form` para a definição. Devolve `null` se o payload não
 * casa o contrato — a página degrada para um erro honesto em vez de renderizar campos meio-formados.
 * Um `any` aqui trocaria uma falha visível por um crash no browser do utilizador.
 */
export const parseBonusFormFields = (data: unknown): BonusFormField[] | null => {
  if (typeof data !== "object" || data === null) {
    return null
  }
  const fields = (data as { fields?: unknown }).fields
  if (!Array.isArray(fields) || !fields.every(isBonusFormField)) {
    return null
  }
  return fields
}

/** Estado inicial: uma chave vazia por campo da definição (o form é controlado). */
export const emptyBonusFormValues = (fields: readonly BonusFormField[]): BonusFormValues =>
  Object.fromEntries(fields.map((field) => [field.name, ""]))

/*
 * SEM MÁSCARA DE TELEFONE, por correção — não por falta de zelo (review da 8.1).
 *
 * Existiu aqui uma máscara BR (`(11) 91234-5678`) que se declarava segura para o número internacional
 * por duas saídas: `+` inicial, ou mais de 11 dígitos. Ambas falham na banda que importa. Um telemóvel
 * português digitado nacionalmente — `912345678`, 9 dígitos, sem `+` — não escapa por nenhuma e saía
 * gravado como `(91) 2345-678`; um fixo do Reino Unido (`2071234567`) saía `(20) 7123-4567`. O
 * `form_data` guardava um número real de um prospect vestido de DDD brasileiro que nunca existiu.
 *
 * NÃO é corrigível por contagem de dígitos: um telemóvel PT de 9 dígitos e um número BR sem DDD são
 * indistinguíveis, e um fixo UK de 10 dígitos tem exatamente a forma de um fixo BR com DDD. O único
 * sinal fiável seria o país — mas `pais` é texto livre e OPCIONAL (ver `BONUS_FORM_FIELDS`), logo não
 * decide nada. A heurística não tem como funcionar; guardar o que o utilizador escreveu, tem.
 *
 * Precedente da casa: o telefone do checkout (`checkout/components/fiscal-billing`) — que está no
 * caminho do dinheiro, onde as apostas são maiores — também persiste sem máscara nenhuma. O campo
 * mantém `type="tel"` + `inputMode="tel"` + `autoComplete="tel"`, que é o que realmente ajuda quem
 * preenche (teclado numérico e autofill), sem tocar no valor.
 *
 * A audiência são prospects traders e o `pais` é um campo do próprio formulário: o erro caro é na
 * direção restritiva, e corromper o contacto de um lead é exatamente esse erro.
 */

/**
 * Valida UM campo (usada no `blur` e reutilizada no submit). Devolve a mensagem PT-BR ou `null`.
 * As mensagens são as MESMAS do Engine, de propósito: o utilizador não deve ver o texto mudar
 * consoante quem validou.
 */
export const validateBonusField = (field: BonusFormField, value: string): string | null => {
  const trimmed = value.trim()

  if (trimmed === "") {
    // Opcional e vazio é VÁLIDO — a obrigatoriedade mínima é deliberada.
    return field.required ? `Informe ${field.label}.` : null
  }

  if (trimmed.length > field.maxLength) {
    return `${field.label} deve ter no máximo ${field.maxLength} caracteres.`
  }

  switch (field.type) {
    case "email":
      return EMAIL_PATTERN.test(trimmed) ? null : `${field.label} inválido.`
    case "select":
      // Vocabulário fechado; um select sem `options` não aceita valor nenhum (espelha o Engine).
      return field.options?.includes(trimmed) ? null : `${field.label} inválido.`
    case "text":
    case "tel":
    case "textarea":
      // `tel` não tem regra de FORMATO, de propósito: o telefone estrangeiro tem de passar tal como
      // o prospect o escreve. Só `required` e `maxLength` (acima) se lhe aplicam.
      return null
  }
}

/** Valida o formulário inteiro. Devolve só os campos com erro (mapa vazio ⇒ pode submeter). */
export const validateBonusForm = (
  fields: readonly BonusFormField[],
  values: BonusFormValues,
): BonusFormErrors => {
  const errors: BonusFormErrors = {}
  for (const field of fields) {
    const error = validateBonusField(field, values[field.name] ?? "")
    if (error !== null) {
      errors[field.name] = error
    }
  }
  return errors
}

/**
 * O 1.º campo inválido pela ordem da DEFINIÇÃO (não pela ordem de inserção no mapa de erros) — é onde
 * o foco tem de ancorar depois de um submit falhado, e a ordem da definição é a ordem do ecrã.
 */
export const firstInvalidFieldName = (
  fields: readonly BonusFormField[],
  errors: BonusFormErrors,
): string | null => fields.find((field) => errors[field.name] !== undefined)?.name ?? null

/**
 * O que se envia ao Engine: valores aparados, com os opcionais vazios OMITIDOS. Espelha exatamente o
 * que o validador do servidor grava — mandar `{"telefone": ""}` faria o servidor gravar uma chave que
 * o utilizador nunca preencheu.
 *
 * Aparar é a ÚNICA transformação: o valor chega ao `form_data` como o prospect o escreveu (ver a nota
 * sobre a máscara acima).
 */
export const toBonusSubmission = (
  fields: readonly BonusFormField[],
  values: BonusFormValues,
): BonusFormValues => {
  const submission: BonusFormValues = {}
  for (const field of fields) {
    const trimmed = (values[field.name] ?? "").trim()
    if (trimmed !== "") {
      submission[field.name] = trimmed
    }
  }
  return submission
}
